// Notifications: live bell drawer showing pending access requests.
// - bell icon badge counts pending requests (subscribed)
// - drawer UI lets the owner assign a role and approve/deny each one
// - approved users are added to the workspace whitelist so they can sign in next time

import { getSession, type ProductRole, approveAccessRequest, denyAccessRequest, subscribeAccessRequests } from '../auth';
import { toast } from './toast';

let _unsub: (() => void) | null = null;
let _rendered: Array<{ id: string; uid?: string; name: string; email: string; note?: string; status: string; createdAt?: number }> = [];
let _drawerOpen = false;

const ROLES: ProductRole[] = ['admin', 'teamLead', 'educator', 'salesAssociate', 'learner'];

export function mountNotificationsBadge(tenantId: string) {
  if (_unsub) { try { _unsub(); } catch {} _unsub = null; }
  _unsub = subscribeAccessRequests(tenantId, (rows) => {
    _rendered = rows as typeof _rendered;
    const pending = _rendered.filter((r) => r.status === 'pending');
    const badge = document.getElementById('notif-badge');
    if (badge) {
      if (pending.length > 0) { badge.textContent = String(pending.length); badge.hidden = false; }
      else { badge.hidden = true; }
    }
    if (_drawerOpen) repaintDrawer();
  });
}

function repaintDrawer() {
  const body = document.querySelector<HTMLElement>('#drawer-notifications .drawer-body');
  if (!body) return;
  body.innerHTML = renderQueue(_rendered);
  wireDrawer();
}

function renderQueue(rows: typeof _rendered): string {
  if (!rows.length) {
    return '<div class="empty" style="padding:32px; text-align:center; color:var(--ink-3);">No access requests yet.</div>';
  }
  return rows.map((r) => {
    const initials = (r.name || r.email || '?').slice(0, 2).toUpperCase();
    const isPending = r.status === 'pending';
    const chooseRole = ROLES.map((rr) => `<option value="${rr}">${rr}</option>`).join('');
    const when = r.createdAt ? new Date(r.createdAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '';
    return `
      <div class="card card-pad-sm req-card" data-req-id="${r.id}" style="margin-bottom:10px;">
        <div class="row">
          <div class="avatar av-c1">${initials}</div>
          <div style="flex:1; min-width:0;">
            <div class="t-strong">${escapeHtml(r.name)}</div>
            <div class="t-meta t-mono">${escapeHtml(r.email)}</div>
            ${r.note ? `<div class="t-meta">${escapeHtml(r.note)}</div>` : ''}
            <div class="t-meta" style="font-size:11px;">${when}</div>
          </div>
          <span class="pill ${r.status === 'approved' ? 'pill-success' : r.status === 'denied' ? 'pill-warn' : 'pill-info'}">${r.status}</span>
        </div>
        ${isPending ? `
          <div class="row" style="gap:8px; margin-top:12px;">
            <label style="display:flex; flex-direction:column; gap:4px; flex:1;">
              <span class="t-eyebrow">Role</span>
              <select class="form-input" data-req-role="${r.id}" style="min-height:34px; padding:4px 8px; font-size:13px;">${chooseRole}</select>
            </label>
            <div style="display:flex; gap:8px;">
              <button class="btn btn-primary btn-sm" data-req-approve="${r.id}">Approve</button>
              <button class="btn btn-ghost btn-sm" data-req-deny="${r.id}">Deny</button>
            </div>
          </div>` : ''}
      </div>
    `;
  }).join('');
}

function escapeHtml(s: string): string {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function wireDrawer() {
  document.querySelectorAll<HTMLButtonElement>('[data-req-approve]').forEach((btn) => {
    if (btn.dataset._wired === '1') return;
    btn.dataset._wired = '1';
    btn.addEventListener('click', async () => {
      const id = btn.getAttribute('data-req-approve');
      if (!id) return;
      const sel = document.querySelector<HTMLSelectElement>(`[data-req-role="${id}"]`);
      const role = (sel?.value || 'learner') as ProductRole;
      const session = getSession();
      if (!session) return;
      btn.disabled = true;
      const old = btn.textContent; btn.textContent = 'Approving…';
      try {
        await approveAccessRequest(session.tenantId, id, role);
        toast('Approved ' + role, 'success');
      } catch (err) {
        toast(err instanceof Error ? err.message : 'Could not approve', 'warn');
        btn.disabled = false; btn.textContent = old || 'Approve';
      }
    });
  });
  document.querySelectorAll<HTMLButtonElement>('[data-req-deny]').forEach((btn) => {
    if (btn.dataset._wired === '1') return;
    btn.dataset._wired = '1';
    btn.addEventListener('click', async () => {
      const id = btn.getAttribute('data-req-deny');
      if (!id) return;
      const session = getSession();
      if (!session) return;
      btn.disabled = true;
      const old = btn.textContent; btn.textContent = 'Denying…';
      try {
        await denyAccessRequest(session.tenantId, id);
        toast('Request denied', 'warn');
      } catch (err) {
        toast(err instanceof Error ? err.message : 'Could not deny', 'warn');
        btn.disabled = false; btn.textContent = old || 'Deny';
      }
    });
  });
}

export function openNotificationsDrawer(tenantId: string) {
  if (!document.getElementById('drawer-notifications')) {
    const wrap = document.createElement('aside');
    wrap.id = 'drawer-notifications';
    wrap.className = 'drawer';
    wrap.setAttribute('role', 'dialog');
    wrap.setAttribute('aria-label', 'Notifications');
    wrap.innerHTML = `
      <div class="drawer-head">
        <div class="avatar avatar-lg av-c2">🔔</div>
        <div style="flex:1; min-width:0;">
          <div class="t-h2">Access requests</div>
          <div class="t-meta">Approve to add them to the workspace whitelist.</div>
        </div>
        <button class="btn btn-ghost btn-sm touch-target" data-action="close-drawer">✕</button>
      </div>
      <div class="drawer-body">${renderQueue(_rendered)}</div>
    `;
    document.body.appendChild(wrap);
    wireDrawer();
  }
  _drawerOpen = true;
  document.querySelectorAll<HTMLElement>('.drawer').forEach((d) => d.classList.remove('is-open'));
  document.getElementById('drawer-notifications')?.classList.add('is-open');
  const scrim = document.getElementById('scrim');
  if (scrim) scrim.classList.add('is-visible');
  document.body.classList.add('drawer-open');
}

export function closeNotificationsDrawer() {
  _drawerOpen = false;
  document.getElementById('drawer-notifications')?.classList.remove('is-open');
  const scrim = document.getElementById('scrim');
  if (scrim) scrim.classList.remove('is-visible');
  document.body.classList.remove('drawer-open');
}
